/**
 * The API seam. Every page reads data through async functions in api/*, never
 * by importing a mock const directly. Today those functions return the mock
 * fixtures (USE_MOCKS = true); flipping VITE_USE_MOCKS=false routes them at the
 * real NestJS API instead — an env change, not a page rewrite. This is the
 * single switch the frontend-first methodology is built around.
 */

import { useSession } from '../stores/session';

/**
 * Resolve mock mode. A PRODUCTION build fails CLOSED — mocks (and the fake-auth
 * fixtures) ship only when VITE_USE_MOCKS=true is explicit. Dev defaults to mocks
 * ON for the frontend-first workflow (opt out with VITE_USE_MOCKS=false).
 */
export function resolveUseMocks(env: { PROD?: boolean; VITE_USE_MOCKS?: string }): boolean {
  if (env.PROD) return env.VITE_USE_MOCKS === 'true';
  return (env.VITE_USE_MOCKS ?? 'true') !== 'false';
}
export const USE_MOCKS = resolveUseMocks(import.meta.env);

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Resolve a value as a promise with a little latency, so loading states are real in mock mode. */
export function mockDelay<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/** Attach the access token (real-API mode) as a Bearer; read fresh per request. */
function authHeaders(): Record<string, string> {
  const token = useSession.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * On a 401 the in-memory access token has expired. auth.ts registers a refresh
 * handler (silent cookie-based rotation); we transparently refresh once and
 * retry the request with the rotated token. Requests to /auth/* never trigger a
 * refresh (so the refresh call itself can't recurse).
 */
let refreshHandler: (() => Promise<boolean>) | null = null;
export function setRefreshHandler(fn: () => Promise<boolean>) {
  refreshHandler = fn;
}

async function doFetch(path: string, init: RequestInit, allowRetry = true): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { ...(init.headers ?? {}), ...authHeaders() },
  });
  if (res.status === 401 && allowRetry && refreshHandler && !path.startsWith('/auth/')) {
    const ok = await refreshHandler();
    if (ok) return doFetch(path, init, false); // one retry with the rotated token
  }
  return res;
}

/** Real-API GET (used when USE_MOCKS is false). The /api/v1 prefix lives here, once. */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await doFetch(path, { method: 'GET' });
  if (!res.ok) throw new ApiError(res.status, `GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/**
 * GET a collection endpoint that returns the `{ items, pagination }` envelope
 * (API_CONTRACT §2) and yield just the items — so pages keep receiving `T[]`
 * exactly as the mock path delivers. Tolerates a bare array too.
 */
export async function apiGetList<T>(path: string): Promise<T[]> {
  const body = await apiGet<{ items: T[] } | T[]>(path);
  return Array.isArray(body) ? body : body.items;
}

/**
 * A business's assignable POS roles (base names) for the create-user role selector (#37 Phase-B).
 * Best-effort: returns [] if unset/unreachable so the selector falls back to a default set.
 */
export async function getBusinessPosRoles(businessId: string): Promise<string[]> {
  if (USE_MOCKS) return mockDelay(['Admin', 'Cashier']);
  try {
    const res = await apiGet<{ ok?: boolean; roles?: string[] }>(`/businesses/${businessId}/pos-roles`);
    return Array.isArray(res?.roles) ? res.roles.filter((r) => typeof r === 'string' && r.length > 0) : [];
  } catch {
    return [];
  }
}

/** Real-API POST (used when USE_MOCKS is false). JSON in, JSON out. */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await doFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, `POST ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Real-API multipart upload. Sends a single file as `file`; the browser sets the
 * multipart Content-Type (incl. boundary), so we must NOT set it ourselves —
 * doFetch only adds the Authorization header.
 */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await doFetch(path, { method: 'POST', body: fd });
  if (!res.ok) throw new ApiError(res.status, `UPLOAD ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Real-API PATCH. */
export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await doFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, `PATCH ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Real-API PUT. */
export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await doFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, `PUT ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Real-API DELETE (tolerates a 204/no-body response). */
export async function apiDelete<T>(path: string): Promise<T> {
  const res = await doFetch(path, { method: 'DELETE' });
  if (!res.ok) throw new ApiError(res.status, `DELETE ${path} failed: ${res.status}`);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}
